import { LifeCycleStage, PromptTemplateType } from '@repo/shared/dtos/organization'
import { Button } from '@repo/ui/button'
import { DataProvider } from '@repo/ui/dataProvider'
import { H2 } from '@repo/ui/h2'
import { useT } from '@repo/ui/hooks'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/tabs'
import { Cfl, sortByKey } from '@repo/ui/utils'
import { LogOut, ShieldAlert } from 'lucide-react'
import { ReactNode, useEffect } from 'react'
import { Link } from 'react-router-dom'

import { Logo } from '@/components/layouts/Logo'
import { OrganizationCreator } from '@/components/sudo/organization/OrganizationCreator'
import { OrganizationList } from '@/components/sudo/organization/OrganizationList'
import { PromptList } from '@/components/sudo/PromptList'
import { UserList } from '@/components/sudo/user/UserList'
import { RouteNames } from '@/routes'
import { Counter } from '@/routes/BeneficiaryRoute'
import { LoadingRoute } from '@/routes/LoadingRoute'
import { RootState } from '@/store'
import { storeActions, sudoStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { SudoContextOrganization, SudoContextUser } from '@/store/sudo'

const SudoTabs = {
  Users: 'users',
  Prompts: 'prompts',
  ...LifeCycleStage,
} as const

export function SudoRoute() {
  const dispatch = useStoreDispatch()
  useEffect(() => {
    void dispatch(sudoStoreActions.initSudo())
  }, [])
  return (
    <Layout>
      <DataProvider
        loadingComponent={<LoadingRoute />}
        selectors={[
          (state: RootState) => state.sudo.organizations,
          (state: RootState) => state.sudo.users,
        ]}
        render={(organizations, users) => (
          <Main
            organizations={[...organizations].sort(sortByKey('name'))}
            users={[...users].sort(sortByKey('lastName'))}
          />
        )}
      />
    </Layout>
  )
}

function Layout({ children }: { children: ReactNode }) {
  return (
    <div className="relative flex flex-1 gap-6 flex-col">
      <Header />

      {children}
    </div>
  )
}

function Main({
  organizations,
  users,
}: {
  organizations: SudoContextOrganization[]
  users: SudoContextUser[]
}) {
  const { tc } = useT()
  const lifeCycleStageTabs = Object.values(LifeCycleStage).sort((a, b) => a.localeCompare(b))
  return (
    <div className="mx-auto flex w-full flex-col gap-4 px-6">
      <div className="flex w-full flex-col gap-4">
        <Tabs defaultValue={lifeCycleStageTabs[0]}>
          <div className="flex items-center gap-4">
            <TabsList>
              {lifeCycleStageTabs.map((stage) => {
                const count = organizations.filter((org) => org.lifeCycleStage === stage).length
                return (
                  <TabsTrigger key={stage} value={stage}>
                    {Cfl(stage.toLowerCase())} <Counter className="bg-primary-100" count={count} />
                  </TabsTrigger>
                )
              })}
            </TabsList>

            <TabsList>
              <TabsTrigger value={SudoTabs.Users}>
                {Cfl(SudoTabs.Users)} <Counter className="bg-primary-100" count={users.length} />
              </TabsTrigger>
            </TabsList>

            <TabsList>
              <TabsTrigger value={SudoTabs.Prompts}>
                {Cfl(SudoTabs.Prompts)}{' '}
                <Counter
                  className="bg-primary-100"
                  count={Object.keys(PromptTemplateType).length}
                />
              </TabsTrigger>
            </TabsList>
          </div>

          {lifeCycleStageTabs.map((stage) => {
            const orgsInStage = organizations
              .filter((org) => org.lifeCycleStage === stage)
              .sort(sortByKey('name'))
            return (
              <TabsContent key={stage} value={stage}>
                <OrganizationList
                  title={stage}
                  description={tc('organization', { cfl: true, count: orgsInStage.length })}
                  organizations={orgsInStage}
                >
                  <OrganizationCreator />
                </OrganizationList>
              </TabsContent>
            )
          })}

          <TabsContent value={SudoTabs.Users}>
            <UserList users={users} />
          </TabsContent>

          <TabsContent value={SudoTabs.Prompts}>
            <PromptList organizations={organizations} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

function Header() {
  const dispatch = useStoreDispatch()
  const { tc } = useT()
  const logout = () => dispatch(storeActions.logout())
  return (
    <div className="bg-primary-950 bg-opacity-90 flex w-full items-center">
      <div className="ml-4 flex">
        <Link
          to={RouteNames.Root}
          className="text-primary my-6 flex items-center justify-center gap-4"
        >
          <Logo />
        </Link>
      </div>
      <div className="flex flex-1 items-center justify-center">
        <H2 className="text-white flex items-center gap-2 border-none">
          <ShieldAlert className="h-8 w-8" />
          Super Admin
        </H2>
      </div>

      <Button variant="link" className="text-white" onClick={() => void logout()}>
        <LogOut className="size-4" />
        {tc('logout')}
      </Button>
    </div>
  )
}
